import {roundTo15MinIntervalStart} from '@hconnect/common/utils'
import {Box} from '@mui/material'
import {Moment} from 'moment-timezone'
import React from 'react'

import {NOW_INDICATOR_REFRESH_INTERVAL_MS} from '../constants'
import {TimeScaleFn} from '../helpers/scale'
import {useCurrentTime} from '../hooks/useCurrentTime'
import {AddGanttChartItemProps, GanttChartBaseRow, GanttChartColorConfig} from '../types/common'

import {GanttChartDataGrid} from './GanttChartDataGrid'

interface GanttChartRowsProps<T extends GanttChartBaseRow, M> {
  chartStartBoundary: M
  chartEndBoundary: M
  stepWidth: number
  isReadOnly: boolean
  xAxis: M[]
  canvasOffset?: number
  onAddItem?: (props: AddGanttChartItemProps<M>) => void | Promise<void>
  ganttChartRows: T[]
  displayCurrentTime?: {
    shouldShowCurrentTime?: boolean
    shouldRoundCurrentTime?: boolean
    timezoneId: string
    xScale: TimeScaleFn
  }
  isAddItemEnabled?: boolean
  rowHeight?: number
  canvasRef?: React.RefObject<HTMLCanvasElement>
  colorConfig?: GanttChartColorConfig[]
  onRenderCallback?: (ctx: CanvasRenderingContext2D) => void
}

export function GanttChartRows<T extends GanttChartBaseRow, M extends Moment | number>({
  chartStartBoundary,
  chartEndBoundary,
  stepWidth,
  isReadOnly,
  xAxis,
  canvasOffset,
  onAddItem,
  ganttChartRows,
  displayCurrentTime,
  isAddItemEnabled = true,
  canvasRef,
  rowHeight,
  colorConfig,
  onRenderCallback
}: GanttChartRowsProps<T, M>) {
  const {xScale, timezoneId, shouldRoundCurrentTime, shouldShowCurrentTime} =
    displayCurrentTime ?? {}

  const currentTime = useCurrentTime({
    timezoneId,
    intervalMs: NOW_INDICATOR_REFRESH_INTERVAL_MS,
    isDisabled: !shouldShowCurrentTime
  })
  const currentTimeAfterRounding =
    shouldRoundCurrentTime && currentTime ? roundTo15MinIntervalStart(currentTime) : currentTime

  const nowOffset = xScale && currentTimeAfterRounding ? xScale(currentTimeAfterRounding) : 0

  return (
    <Box sx={{position: 'absolute', top: 0, left: 0}}>
      <GanttChartDataGrid<T, M>
        xAxis={xAxis}
        canvasOffset={canvasOffset}
        chartEndBoundary={chartEndBoundary}
        stepWidth={stepWidth}
        chartStartBoundary={chartStartBoundary}
        canvasRef={canvasRef}
        displayCurrentTime={{
          nowOffset,
          timezoneId
        }}
        colorConfig={colorConfig}
        isDisabled={isReadOnly}
        onAddItem={onAddItem}
        isAddItemEnabled={isAddItemEnabled}
        ganttChartRows={ganttChartRows}
        rowHeight={rowHeight}
        onRenderCallback={onRenderCallback}
      />
    </Box>
  )
}

GanttChartRows.displayName = 'GanttChartRows'
